import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../common/Button';
import useAuth from '../../hooks/useAuth';
import { followUser, unfollowUser } from '../../api/users';

/**
 * FollowButton component for following/unfollowing users
 * @param {Object} props - Component props
 * @param {string} props.userId - ID of the user to follow/unfollow
 * @param {boolean} props.isFollowing - Whether the current user is following this user
 * @param {Function} props.onFollowChange - Callback for follow status change
 * @param {string} props.size - Button size
 * @returns {JSX.Element} FollowButton component
 */
const FollowButton = ({ 
  userId, 
  isFollowing = false,
  onFollowChange,
  size = 'sm'
}) => {
  const { user, isAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [following, setFollowing] = useState(isFollowing);
  const [error, setError] = useState('');
  
  // Don't show button for own profile
  if (user && user._id === userId) {
    return null;
  }
  
  // Handle follow/unfollow
  const handleToggleFollow = async () => {
    if (!isAuth) {
      // Redirect to login or show login modal
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      if (following) {
        await unfollowUser(userId);
      } else {
        await followUser(userId);
      }
      
      // Update state
      setFollowing(!following);
      
      // Call callback if provided
      if (onFollowChange) {
        onFollowChange(!following);
      }
    } catch (error) {
      console.error('Follow toggle error:', error);
      setError(error.response?.data?.message || 'Failed to update follow status');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <>
      <Button
        variant={following ? 'outline' : 'primary'}
        size={size}
        onClick={handleToggleFollow}
        isLoading={loading}
        isDisabled={loading || !isAuth}
      >
        {following ? 'Following' : 'Follow'}
      </Button>
      
      {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
    </>
  );
};

FollowButton.propTypes = {
  userId: PropTypes.string.isRequired,
  isFollowing: PropTypes.bool,
  onFollowChange: PropTypes.func,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
};

export default FollowButton;